import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Box, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import SettingsIcon from "@mui/icons-material/Settings";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Normalise EligibleWorkflow object → "group/name" path string */
const toPath = (wf) => `${wf.workflowGroup}/${wf.workflowName}`;

/** Normalise "group/name" path string → EligibleWorkflow object */
const fromPath = (path) => {
  const [workflowGroup, ...rest] = path.split("/");
  return { workflowGroup, workflowName: rest.join("/") };
};

/**
 * Takes a flat string[] of "group/workflowName" entries and returns
 * { [group]: string[] } sorted alphabetically within each group.
 */
function groupWorkflowItems(items) {
  const grouped = {};
  items.forEach((wf) => {
    const [group, ...nameParts] = String(wf).split("/");
    const wfName = nameParts.join("/");
    if (!group || !wfName) return;
    if (!grouped[group]) grouped[group] = [];
    grouped[group].push(wfName);
  });
  Object.keys(grouped).forEach((k) => grouped[k].sort((a, b) => a.localeCompare(b)));
  return grouped;
}

// ---------------------------------------------------------------------------
// SimpleTreePanel — same layout as ScheduleEditorFullScreen workflow trees
// ---------------------------------------------------------------------------

function SimpleTreePanel({
  title,
  groupLabel,
  groupedItems,
  iconType,
  onToggleItem,
  onToggleGroup,
  loading = false,
}) {
  const [expandedGroups, setExpandedGroups] = useState({});
  const groups = Object.keys(groupedItems);
  const groupsKey = groups.join("|");

  useEffect(() => {
    const g = Object.keys(groupedItems);
    const next = {};
    const autoExpandSingleGroup = g.length === 1;
    g.forEach((name) => {
      next[name] = autoExpandSingleGroup;
    });
    setExpandedGroups(next);
    // Only reset when the set of group keys changes (not on every groupedItems reference change).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupsKey]);

  return (
    <Paper variant="outlined" sx={{ borderRadius: 1, borderColor: "#cfd5df", overflow: "hidden", bgcolor: "#fff" }}>
      <Box sx={{ px: 1.25, py: 0.85, borderBottom: "1px solid #dde2ea", bgcolor: "#edf0f5" }}>
        <Typography sx={{ fontWeight: 700, fontSize: "0.78rem", color: "#616a79", textTransform: "uppercase", letterSpacing: 0.3 }}>
          {title}
        </Typography>
      </Box>
      <Box sx={{ height: 260, maxHeight: 260, overflow: "auto", p: 0.75 }}>
        {loading ? (
          <Box sx={{ height: "100%", minHeight: 160, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <CircularProgress size={20} />
          </Box>
        ) : null}
        {!loading
          ? Object.entries(groupedItems).map(([group, names]) => (
              <Box key={group} sx={{ mb: 0.45 }}>
                <Box
                  sx={{
                    px: 0.4,
                    minHeight: 26,
                    display: "grid",
                    gridTemplateColumns: "1fr",
                    alignItems: "center",
                    columnGap: 0.2,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.4,
                      color: "#5b6473",
                      userSelect: "none",
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    <Box sx={{ width: 18, display: "flex", justifyContent: "center", alignItems: "center", flexShrink: 0 }}>
                      <Box
                        component="button"
                        type="button"
                        onClick={() => setExpandedGroups((prev) => ({ ...prev, [group]: !prev[group] }))}
                        title={expandedGroups[group] ? "Collapse folder" : "Expand folder"}
                        sx={{
                          p: 0,
                          m: 0,
                          border: 0,
                          background: "transparent",
                          color: "#7b8493",
                          cursor: "pointer",
                          lineHeight: 0,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {expandedGroups[group] ? (
                          <ExpandMoreIcon sx={{ fontSize: 16 }} />
                        ) : (
                          <ChevronRightIcon sx={{ fontSize: 16 }} />
                        )}
                      </Box>
                    </Box>
                    <Box sx={{ width: 16, display: "flex", justifyContent: "center", alignItems: "center", flexShrink: 0 }}>
                      <FolderOpenIcon sx={{ fontSize: 16, color: "#7b8493" }} />
                    </Box>
                    <Typography
                      variant="caption"
                      onClick={() => setExpandedGroups((prev) => ({ ...prev, [group]: !prev[group] }))}
                      sx={{ fontWeight: 700, lineHeight: 1.2, cursor: "pointer", flex: 1, minWidth: 0 }}
                    >
                      {groupLabel || group}
                    </Typography>
                    <Box
                      component="button"
                      type="button"
                      onClick={() => onToggleGroup(group, names)}
                      title={iconType === "add" ? "Add entire folder" : "Remove entire folder"}
                      sx={{
                        p: 0,
                        m: 0,
                        border: 0,
                        background: "transparent",
                        color: "#69707d",
                        cursor: "pointer",
                        lineHeight: 0,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {iconType === "add" ? (
                        <AddCircleOutlineIcon sx={{ fontSize: 18 }} />
                      ) : (
                        <RemoveCircleOutlineIcon sx={{ fontSize: 18 }} />
                      )}
                    </Box>
                  </Box>
                </Box>
                <Box
                  sx={{
                    mt: 0.15,
                    pl: 1.2,
                    display: expandedGroups[group] ? "block" : "none",
                    width: "100%",
                  }}
                >
                  {names.map((name) => (
                    <Box
                      key={`${group}/${name}`}
                      sx={{
                        width: "88%",
                        mx: "auto",
                        display: "grid",
                        gridTemplateColumns: "1fr",
                        alignItems: "center",
                        columnGap: 0.2,
                        color: "#4f5867",
                        px: 0.6,
                        py: 0.2,
                        minHeight: 26,
                        borderRadius: 0.6,
                        "&:hover": { bgcolor: "#f7f9fc" },
                      }}
                    >
                      <Stack direction="row" spacing={0.6} alignItems="center" sx={{ width: "100%" }}>
                        <Box sx={{ width: 16, display: "flex", justifyContent: "center", alignItems: "center", flexShrink: 0 }}>
                          <SettingsIcon sx={{ fontSize: 14, color: "#8a93a2" }} />
                        </Box>
                        <Typography variant="body2" sx={{ textAlign: "left", fontSize: "0.82rem", flex: 1, minWidth: 0 }}>
                          {name}
                        </Typography>
                        <Box
                          component="button"
                          type="button"
                          onClick={() => onToggleItem(`${group}/${name}`)}
                          title={iconType === "add" ? "Add item" : "Remove item"}
                          sx={{
                            p: 0,
                            m: 0,
                            border: 0,
                            background: "transparent",
                            color: "#69707d",
                            cursor: "pointer",
                            lineHeight: 0,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          {iconType === "add" ? (
                            <AddCircleOutlineIcon sx={{ fontSize: 18 }} />
                          ) : (
                            <RemoveCircleOutlineIcon sx={{ fontSize: 18 }} />
                          )}
                        </Box>
                      </Stack>
                    </Box>
                  ))}
                </Box>
              </Box>
            ))
          : null}
      </Box>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// WorkflowAssignmentPanel — public reusable component
// ---------------------------------------------------------------------------

export default function WorkflowAssignmentPanel({
  availableWorkflows,
  assignedWorkflows,
  onChange,
  loading,
}) {
  const allPaths = availableWorkflows.map(toPath);
  const assignedPaths = assignedWorkflows.map(toPath);
  const unassignedPaths = allPaths.filter((p) => !assignedPaths.includes(p));

  const handleAddItem = (path) => {
    onChange([...assignedWorkflows, fromPath(path)]);
  };

  const handleRemoveItem = (path) => {
    onChange(assignedWorkflows.filter((wf) => toPath(wf) !== path));
  };

  const handleAddGroup = (group, names) => {
    const newPaths = names.map((n) => `${group}/${n}`);
    const merged = Array.from(new Set([...assignedPaths, ...newPaths])).sort((a, b) =>
      a.localeCompare(b)
    );
    onChange(merged.map(fromPath));
  };

  const handleRemoveGroup = (group, names) => {
    const toRemove = new Set(names.map((n) => `${group}/${n}`));
    onChange(assignedWorkflows.filter((wf) => !toRemove.has(toPath(wf))));
  };

  return (
    <Box>
      <Typography sx={{ fontWeight: 700, color: "#5d6675", mb: 1 }}>Workflows</Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
        <SimpleTreePanel
          title="Unassigned WorkFlows"
          groupedItems={groupWorkflowItems(unassignedPaths)}
          iconType="add"
          loading={loading}
          onToggleItem={handleAddItem}
          onToggleGroup={handleAddGroup}
        />
        <SimpleTreePanel
          title="Assigned WorkFlows"
          groupedItems={groupWorkflowItems(assignedPaths)}
          iconType="remove"
          loading={loading}
          onToggleItem={handleRemoveItem}
          onToggleGroup={handleRemoveGroup}
        />
      </Box>
    </Box>
  );
}

WorkflowAssignmentPanel.propTypes = {
  availableWorkflows: PropTypes.arrayOf(
    PropTypes.shape({
      workflowGroup: PropTypes.string.isRequired,
      workflowName: PropTypes.string.isRequired,
    })
  ).isRequired,
  assignedWorkflows: PropTypes.arrayOf(
    PropTypes.shape({
      workflowGroup: PropTypes.string.isRequired,
      workflowName: PropTypes.string.isRequired,
    })
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};
